/**
 * Manager · Menu Availability (iter280)
 *
 * Lists published menus at the manager's outlet and lets them mark
 * items 86'd (unavailable). Reuses the existing UnavailableModal /
 * PATCH endpoint pattern from Admin Menu Studio, so 86'd items
 * disappear from the guest URL end-to-end.
 *
 * Only visible when the outlet's Menu module is enabled.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { AlertTriangle, ChevronDown, ChevronUp, RefreshCw, UtensilsCrossed,
         Search, X, Copy, Share2, QrCode } from 'lucide-react';
import { QrDialog } from '../components/menu/QrDialog';

const PRIMARY = 'var(--color-primary)';

export default function ManagerMenuAvailability() {
  const { API, getHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [outlet, setOutlet] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [menus, setMenus] = useState([]);
  const [menuSlug, setMenuSlug] = useState('');
  const [expanded, setExpanded] = useState({});
  const [details, setDetails] = useState({});           // menu_id → detail
  const [unavailModal, setUnavailModal] = useState(null); // {menuId, itemId, itemName}
  const [refreshing, setRefreshing] = useState(false);
  // ITER288 — Guest Menu share flow
  const [qrOpen, setQrOpen] = useState(false);
  const [qrData, setQrData] = useState(null);
  // iter281 — client-side search across items in the currently-loaded
  // menu details. Filters case-insensitively against item.name; hides
  // categories that have zero matches.
  const [query, setQuery] = useState('');
  const fetchingRef = useRef(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/manager/menus`, { headers: getHeaders() });
      setOutlet(res.data.outlet || null);
      setEnabled(!!res.data.menu_enabled);
      setMenus(res.data.menus || []);
      setMenuSlug(res.data.menu_slug || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load menus');
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = async (menuId) => {
    setExpanded((e) => ({ ...e, [menuId]: !e[menuId] }));
    if (!details[menuId]) {
      try {
        const res = await axios.get(`${API}/manager/menus/${menuId}`,
                                    { headers: getHeaders() });
        setDetails((d) => ({ ...d, [menuId]: res.data }));
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load menu');
      }
    }
  };

  const reloadDetail = useCallback(async (menuId) => {
    if (fetchingRef.current.has(menuId)) return;
    fetchingRef.current.add(menuId);
    try {
      const res = await axios.get(`${API}/manager/menus/${menuId}`,
                                  { headers: getHeaders() });
      setDetails((d) => ({ ...d, [menuId]: res.data }));
    } catch { /* ignore */ }
    finally { fetchingRef.current.delete(menuId); }
  }, [API, getHeaders]);

  const q = query.trim().toLowerCase();

  // When searching, auto-expand all menus and load any missing details
  // so the filter works across every menu, not just the open ones.
  useEffect(() => {
    if (!q || menus.length === 0) return;
    setExpanded((e) => {
      const next = { ...e };
      menus.forEach((m) => { next[m.id] = true; });
      return next;
    });
    menus.forEach((m) => { if (!details[m.id]) reloadDetail(m.id); });
  }, [q, menus, details, reloadDetail]);

  const filterCategories = (cats) => {
    if (!q) return cats || [];
    return (cats || [])
      .map((c) => ({
        ...c,
        items: (c.items || []).filter((it) => (it.name || '').toLowerCase().includes(q)),
      }))
      .filter((c) => c.items.length > 0);
  };

  const markAvailable = async (menuId, itemId) => {
    try {
      await axios.patch(
        `${API}/admin/menus/${menuId}/items/${itemId}/availability`,
        { available: true },
        { headers: getHeaders() },
      );
      toast.success('Item restored');
      reloadDetail(menuId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to restore item');
    }
  };

  const submitUnavailable = async ({ mode, reason }) => {
    if (!unavailModal) return;
    try {
      await axios.patch(
        `${API}/admin/menus/${unavailModal.menuId}/items/${unavailModal.itemId}/availability`,
        { available: false, unavailable_mode: mode, unavailable_reason: reason },
        { headers: getHeaders() },
      );
      toast.success(mode === 'shift'
        ? 'Marked unavailable for this shift (auto-restores in 12h)'
        : 'Marked unavailable indefinitely');
      const menuId = unavailModal.menuId;
      setUnavailModal(null);
      reloadDetail(menuId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update availability');
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await load();
    // Also refresh any expanded details.
    const openIds = Object.keys(expanded).filter((k) => expanded[k]);
    for (const id of openIds) {
      await reloadDetail(id);
    }
    setRefreshing(false);
  };

  /* ── ITER288 — Guest Menu share helpers ─────────────────────────── */
  // Public URL derives from the same origin the manager is browsing on,
  // so it always matches what the guest would type. In future we could
  // swap the origin for a branded `menu.restropulse.online` host without
  // touching this page — the backend `_public_base_url()` already exposes
  // that override for the QR endpoint.
  const guestMenuUrl = menuSlug
    ? `${window.location.origin}/menu/${menuSlug}`
    : '';

  const copyGuestMenuLink = async () => {
    if (!guestMenuUrl) return;
    try {
      await navigator.clipboard.writeText(guestMenuUrl);
      toast.success('Guest menu link copied successfully.');
    } catch {
      toast.error('Could not copy — please copy manually.');
    }
  };

  const shareGuestMenu = async () => {
    if (!guestMenuUrl) return;
    const shareText =
      `Explore our menu before your visit.\nView our menu here:\n${guestMenuUrl}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${outlet?.name || 'Menu'} · Guest Menu`,
          text: shareText,
          url: guestMenuUrl,
        });
      } catch {
        /* user dismissed — no toast needed */
      }
      return;
    }
    // Fallback: copy the full share text (URL included).
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success('Sharing is not supported here — link + message copied to your clipboard.');
    } catch {
      toast.error('Could not share — please copy the link manually.');
    }
  };

  const openQr = async () => {
    try {
      const res = await axios.get(
        `${API}/manager/menu-qr?request_url=${encodeURIComponent(window.location.origin)}`,
        { headers: getHeaders() },
      );
      setQrData(res.data);
      setQrOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'QR generation failed');
    }
  };

  if (loading) {
    return <div className="p-10 text-center text-gray-500">Loading…</div>;
  }

  if (!enabled) {
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="menu-availability-disabled">
        <Card>
          <CardContent className="p-8 text-center space-y-2">
            <UtensilsCrossed className="w-8 h-8 mx-auto text-gray-400" />
            <div className="font-medium" style={{ color: PRIMARY }}>
              Digital Menu isn&apos;t enabled for this outlet yet.
            </div>
            <div className="text-sm text-gray-500">
              Ask an admin to enable the Menu module in Menu Studio.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-5" data-testid="manager-menu-availability">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: PRIMARY }}>
            Menu Availability
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Mark dishes 86&apos;d for the current shift or indefinitely.
            Shift-mode items auto-restore after 12 hours.
            {outlet?.name && <> · <span className="font-medium">{outlet.name}</span></>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing}
                data-testid="menu-availability-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* ITER288 — Guest Menu share card. Only rendered when the outlet
          has a `menu_slug` (i.e. Digital Menu is enabled and a public
          URL exists). No admin/manager routes leak — only the public
          `/menu/{slug}` URL is exposed. */}
      {guestMenuUrl && (
        <Card data-testid="guest-menu-share-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <div className="text-[11px] uppercase tracking-widest text-gray-500 font-medium">
                  Guest Menu
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  Share this link with guests to preview the menu before their visit.
                </div>
              </div>
              <div className="flex-[2] min-w-[280px] flex items-stretch gap-2">
                <Input readOnly value={guestMenuUrl}
                       onFocus={(e) => e.target.select()}
                       className="font-mono text-xs bg-gray-50"
                       data-testid="guest-menu-url" />
                <Button variant="outline" size="sm" onClick={copyGuestMenuLink}
                        data-testid="guest-menu-copy-btn"
                        title="Copy link to clipboard">
                  <Copy className="w-4 h-4 mr-1" /><span className="hidden sm:inline">Copy</span>
                </Button>
                <Button size="sm" onClick={shareGuestMenu}
                        style={{ background: PRIMARY, color: '#fff' }}
                        data-testid="guest-menu-share-btn"
                        title="Share menu">
                  <Share2 className="w-4 h-4 mr-1" /><span className="hidden sm:inline">Share</span>
                </Button>
                <Button variant="outline" size="sm" onClick={openQr}
                        data-testid="guest-menu-qr-btn"
                        title="View QR code">
                  <QrCode className="w-4 h-4 mr-1" /><span className="hidden sm:inline">QR</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* iter281 — Search box. Filters items by name (case-insensitive
          substring). Client-side only, no backend call. */}
      <div className="relative" data-testid="menu-search">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <Input value={query}
               onChange={(e) => setQuery(e.target.value)}
               placeholder="Search items..."
               className="pl-9 pr-9 h-11"
               data-testid="menu-search-input" />
        {query && (
          <button type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100"
                  aria-label="Clear search"
                  data-testid="menu-search-clear">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        )}
      </div>

      {menus.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-gray-400">
            No published menus at this outlet yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {menus.map((m) => {
            const isOpen = !!expanded[m.id];
            const d = details[m.id];
            return (
              <Card key={m.id} data-testid={`menu-row-${m.id}`}>
                <button type="button"
                        onClick={() => toggleExpand(m.id)}
                        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 text-left"
                        data-testid={`menu-row-toggle-${m.id}`}>
                  <div>
                    <div className="font-medium" style={{ color: PRIMARY }}>{m.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {m.categories_count || 0} categories · {m.items_count || 0} items
                    </div>
                  </div>
                  {isOpen
                    ? <ChevronUp className="w-4 h-4 text-gray-400" />
                    : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </button>
                {isOpen && (
                  <CardContent className="pt-0 pb-5">
                    {!d ? (
                      <div className="text-sm text-gray-400 text-center py-4">Loading…</div>
                    ) : (
                      <div className="space-y-4">
                        {filterCategories(d.parsed_data?.categories).map((c) => (
                          <div key={c.id || c.name}>
                            <div className="text-[11px] uppercase tracking-widest text-gray-500 mb-1">
                              {c.name}
                            </div>
                            <div className="divide-y border rounded-md">
                              {(c.items || []).map((it) => (
                                <div key={it.id} className="flex items-center justify-between px-3 py-2 gap-3"
                                     data-testid={`item-row-${it.id}`}>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <div className={`text-sm ${it.available === false ? 'line-through text-gray-400' : ''}`}>
                                        {it.name}
                                      </div>
                                      {it.available === false && (
                                        <span className="text-[10px] px-1.5 rounded-full bg-rose-100 text-rose-700 font-medium">
                                          {it.unavailable_mode === 'shift' ? 'Sold Out · Shift'
                                            : it.unavailable_mode === 'indefinite' ? 'Sold Out · Indefinite'
                                            : 'Sold Out'}
                                        </span>
                                      )}
                                    </div>
                                    {it.description && (
                                      <div className="text-xs text-gray-500 mt-0.5">{it.description}</div>
                                    )}
                                    {it.available === false && it.unavailable_reason && (
                                      <div className="text-[11px] text-rose-600 italic mt-0.5">
                                        Reason: {it.unavailable_reason}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 shrink-0">
                                    <div className="text-sm font-medium" style={{ color: PRIMARY }}>
                                      {it.price_display || (it.price ? `₹${it.price}` : '—')}
                                    </div>
                                    {it.available === false ? (
                                      <Button size="sm" variant="outline"
                                              onClick={() => markAvailable(m.id, it.id)}
                                              className="text-emerald-700 border-emerald-400 hover:bg-emerald-50"
                                              data-testid={`mgr-restore-${it.id}`}>
                                        Restore
                                      </Button>
                                    ) : (
                                      <Button size="sm" variant="outline"
                                              onClick={() => setUnavailModal({
                                                menuId: m.id, itemId: it.id, itemName: it.name,
                                              })}
                                              data-testid={`mgr-mark-sold-out-${it.id}`}>
                                        Mark Sold Out
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                        {filterCategories(d.parsed_data?.categories).length === 0 && (
                          <div className="text-center text-sm text-gray-400 py-4"
                               data-testid={`menu-no-results-${m.id}`}>
                            {q ? 'No items match your search.' : 'This menu has no parsed items yet.'}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {unavailModal && (
        <UnavailableModal itemName={unavailModal.itemName}
                          onClose={() => setUnavailModal(null)}
                          onSubmit={submitUnavailable} />
      )}

      <QrDialog
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        data={qrData}
        outletName={outlet?.name}
      />
    </div>
  );
}

/* ------ Modal (kept local to keep Manager UI self-contained) ------ */

function UnavailableModal({ itemName, onClose, onSubmit }) {
  const [mode, setMode] = useState('shift');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await onSubmit({ mode, reason: reason.trim() }); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="mgr-unavail-modal">
        <DialogHeader>
          <DialogTitle style={{ color: PRIMARY }}>
            <span className="inline-flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-600" />
              Mark &ldquo;{itemName}&rdquo; unavailable
            </span>
          </DialogTitle>
          <DialogDescription>
            The item will be hidden from the guest menu until restored.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm">Duration</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button type="button" onClick={() => setMode('shift')}
                      data-testid="mgr-mode-shift"
                      className={`text-left rounded-lg border p-3 text-sm transition ${
                        mode === 'shift' ? 'border-emerald-500 bg-emerald-50'
                                         : 'border-gray-200 hover:bg-gray-50'
                      }`}>
                <div className="font-medium">This shift only</div>
                <div className="text-[11px] text-gray-500 mt-0.5">Auto-restores after 12 hours</div>
              </button>
              <button type="button" onClick={() => setMode('indefinite')}
                      data-testid="mgr-mode-indefinite"
                      className={`text-left rounded-lg border p-3 text-sm transition ${
                        mode === 'indefinite' ? 'border-rose-500 bg-rose-50'
                                              : 'border-gray-200 hover:bg-gray-50'
                      }`}>
                <div className="font-medium">Indefinite</div>
                <div className="text-[11px] text-gray-500 mt-0.5">Stays hidden until manually restored</div>
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Reason (optional)</Label>
            <Input value={reason}
                   onChange={(e) => setReason(e.target.value.slice(0, 200))}
                   placeholder="e.g. Ingredient sold out"
                   data-testid="mgr-unavail-reason" />
            <div className="text-[11px] text-gray-400 text-right">{reason.length}/200</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving}
                  style={{ background: '#B91C1C', color: '#fff' }}
                  data-testid="mgr-unavail-confirm">
            {saving ? 'Saving…' : 'Mark Unavailable'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
